import { z } from 'zod';
/**
 * Mirrors `userIdSchema` (see `./user-id.ts`): user ids are Clerk ids, not
 * UUIDs. Inlined rather than imported because this file is loaded by the
 * `ai-recipes` Edge Function, and Deno cannot resolve the `.js` specifiers
 * TypeScript requires — every shared file consumed by a function stays
 * dependency-free.
 */
const memberIdSchema = z.string().min(1);
const RECIPE_CATEGORIES = [
    'starter',
    'main_dish',
    'side_dish',
    'dessert',
    'snack',
    'drink',
    'sauce',
    'bread',
    'soup',
];
const SEASONS = ['spring', 'summer', 'autumn', 'winter'];
const INGREDIENT_UNITS = [
    'g',
    'kg',
    'ml',
    'cl',
    'l',
    'tsp',
    'tbsp',
    'cup',
    'oz',
    'lb',
    'fl_oz',
    'piece',
    'pinch',
    'bunch',
    'slice',
    'clove',
    'other',
];
const MEAL_TYPES = ['lunch', 'dinner'];
// ---------------------------------------------------------------------------
// Imported recipe structure (returned by providers / GPT structured output)
// ---------------------------------------------------------------------------
export const ImportedIngredientSchema = z.object({
    name: z.string().describe('Ingredient name'),
    quantity_value: z.number().nullable().describe('Exact quantity or null'),
    quantity_min: z.number().nullable().describe('Range min or null'),
    quantity_max: z.number().nullable().describe('Range max or null'),
    unit: z.enum(INGREDIENT_UNITS).nullable().describe('Measurement unit'),
    unit_label: z.string().nullable().describe('Free-text unit if unit is "other"'),
});
export const ImportedIngredientGroupSchema = z.object({
    title: z.string().nullable().describe('Group label, e.g. "Pour la pâte"'),
    ingredients: z.array(ImportedIngredientSchema),
});
export const ImportedStepSchema = z.object({
    instruction: z.string().describe('Step instruction text'),
    image_url: z.string().nullable().describe('Step illustration URL or null'),
});
export const ImportedStepSectionSchema = z.object({
    title: z.string().nullable().describe('Section label or null'),
    steps: z.array(ImportedStepSchema),
});
export const ImportedRecipeSchema = z.object({
    title: z.string().describe('Recipe title'),
    description: z.string().nullable().describe('Short description'),
    category: z.enum(RECIPE_CATEGORIES).describe('Recipe category'),
    tags: z.array(z.string()).describe('Free-text tags'),
    images: z.array(z.string()).describe('Image URLs'),
    seasons: z.array(z.enum(SEASONS)).describe('Applicable seasons'),
    total_time: z.number().nullable().describe('Total time in minutes'),
    waiting_time: z.number().nullable().describe('Waiting/resting time in minutes'),
    cooking_time: z.number().nullable().describe('Cooking time in minutes'),
    baking_time: z.number().nullable().describe('Baking/prep time in minutes'),
    servings_value: z.number().describe('Default number of servings'),
    servings_unit: z.string().describe('Servings unit: personnes, parts, pièces'),
    servings_text: z.string().nullable().describe('Display text, e.g. "Pour 4 personnes"'),
    ingredient_groups: z.array(ImportedIngredientGroupSchema),
    step_sections: z.array(ImportedStepSectionSchema),
    source_name: z.string().nullable().describe('Source website name'),
    source_url: z.string().nullable().describe('Source URL'),
});
// ---------------------------------------------------------------------------
// Optimized instructions (partial recipe — steps only)
// ---------------------------------------------------------------------------
export const OptimizedStepSchema = z.object({
    instruction: z.string().describe('Reformulated step instruction'),
    duration_seconds: z.number().nullable().describe('Duration in seconds if mentioned'),
    note: z.string().nullable().describe('Tip or note for this step'),
});
export const OptimizedSectionSchema = z.object({
    title: z.string().nullable().describe('Section title or null'),
    steps: z.array(OptimizedStepSchema),
});
export const OptimizedInstructionsSchema = z.object({
    step_sections: z.array(OptimizedSectionSchema),
});
// ---------------------------------------------------------------------------
// Menu generation
// ---------------------------------------------------------------------------
export const MenuSlotSchema = z.object({
    day: z.string().describe('ISO date string YYYY-MM-DD'),
    meal_type: z.enum(MEAL_TYPES).describe('lunch or dinner'),
    recipe_id: z.string().describe('UUID of the selected recipe'),
    reason: z.string().describe('Short justification for this choice'),
});
/** A slot the member asked to generate, without a recipe attached yet. */
export const MenuSlotTargetSchema = z.object({
    day: z.string().describe('ISO date string YYYY-MM-DD'),
    meal_type: z.enum(MEAL_TYPES).describe('lunch or dinner'),
});
/** A slot holding a meal planned without a recipe, which a menu leaves as it is. */
export const FreeMenuSlotSchema = MenuSlotTargetSchema.extend({
    title: z.string().trim().min(1).max(80).describe('Name the family gave that meal'),
});
/** MenuSlot enriched with recipe metadata (returned by generate-menu). */
export const EnrichedMenuSlotSchema = MenuSlotSchema.extend({
    /**
     * Set on a lunch that eats the leftovers of the dinner the evening before
     * (`leftovers` wish): same recipe, cooked once for both tables.
     */
    leftover_of: MenuSlotTargetSchema.optional(),
    recipe_title: z.string(),
    recipe_image_url: z.string().nullable(),
    recipe_category: z.string(),
    recipe_total_time: z.number().nullable(),
    recipe_servings_value: z.number().nullable(),
});
/**
 * What generate-menu answers: the proposal, and the requested slots it left
 * empty on purpose because nobody sits down to them — by the members' usual
 * weeks or a table set by hand.
 */
export const GeneratedMenuResponseSchema = z.object({
    slots: z.array(EnrichedMenuSlotSchema),
    skipped: z.array(MenuSlotTargetSchema).default([]),
});
/**
 * Why generate-menu refused, as a machine reason next to the error code, so
 * the app can say it in the reader's language rather than relay the server's
 * English.
 */
export const MENU_GENERATION_REFUSALS = {
    notEnoughRecipes: 'not_enough_recipes',
    nobodyAtTable: 'nobody_at_table',
    noSlotLeft: 'no_slot_left',
};
/** Recipes the family carnet must hold before a menu can be composed from it. */
export const MENU_GENERATION_MIN_RECIPES = 10;
export const GeneratedMenuSchema = z.object({
    slots: z.array(MenuSlotSchema),
});
/**
 * What the model fills when it composes a menu. Recipes are named by their
 * short library reference (« R12 »), and their title is copied before the
 * reason: a uuid gave the model nothing to remember the dish by, and its
 * reasons drifted onto other dishes (« repas végétarien » for a chicken).
 * The slot's need comes first, so the dish is chosen to answer it rather
 * than the reason being written to fit the slot after the fact.
 */
export const MenuDraftSchema = z.object({
    slots: z.array(z.object({
        day: z.string().describe('ISO date string YYYY-MM-DD'),
        meal_type: z.enum(MEAL_TYPES).describe('lunch or dinner'),
        need: z
            .string()
            .describe('What this slot calls for, decided first (e.g. "fish, quick, Friday dinner")'),
        recipe: z.string().describe('Library reference of a recipe that answers that need, e.g. R12'),
        title: z.string().describe('Title of that recipe, copied from the library'),
        reason: z
            .string()
            .describe('Why THIS dish fits, true to its title: never claim fish or vegetarian it is not'),
    })),
});
// ---------------------------------------------------------------------------
// Request payloads (Hono zValidator)
// ---------------------------------------------------------------------------
export const ImportFromUrlPayloadSchema = z.object({
    url: z.string().url(),
    save: z.boolean().default(false),
});
export const ImportFromImagePayloadSchema = z.object({
    images: z
        .array(z.string().min(1))
        .min(1)
        .max(5)
        .describe('Base64-encoded images (data:image/... or raw base64)'),
});
/**
 * What the family asks the generated menu to lean towards. Each one is read
 * by the prompt as an instruction of its own (see `buildMenuGenerationPrompt`).
 */
export const MENU_ORIENTATIONS = [
    'balanced',
    'quick',
    'budget',
    'seasonal',
    'favorites',
    'kids',
];
/**
 * Cooking in double: whether each lunch eats the leftovers of the dinner the
 * evening before — never, Monday to Friday, or every day.
 */
export const MENU_LEFTOVER_MODES = ['off', 'weekdays', 'every_day'];
/** The language the AI writes its reasons in: the reader's. */
export const AI_LOCALES = ['fr', 'en'];
export const GenerateMenuPayloadSchema = z.object({
    start_date: z.string().date(),
    end_date: z.string().date(),
    meal_types: z.array(z.enum(MEAL_TYPES)).default(['lunch', 'dinner']),
    member_ids: z.array(memberIdSchema).min(1),
    locked_slots: z.array(MenuSlotSchema).default([]),
    /**
     * Meals planned without a recipe (« Barbecue », « Apéro dînatoire ») on the
     * period: never filled, and read by the model for the variety around them.
     */
    free_slots: z.array(FreeMenuSlotSchema).default([]),
    /**
     * Exact slots to fill. When empty the whole [start_date, end_date] range is
     * generated for every `meal_types` entry; when provided it wins over both,
     * so the member can pick precise days and meal slots (spec 05).
     */
    target_slots: z.array(MenuSlotTargetSchema).default([]),
    orientations: z.array(z.enum(MENU_ORIENTATIONS)).default([]),
    /** Free text from the family, read as a wish the hard constraints still bound. */
    instructions: z.string().trim().max(500).optional(),
    /** Each targeted lunch eats the dinner of the evening before (`MENU_LEFTOVER_MODES`). */
    leftovers: z.enum(MENU_LEFTOVER_MODES).default('off'),
    locale: z.enum(AI_LOCALES).default('fr'),
});
/** Why a dish is being swapped: the AI reads it as the brief for its one proposal. */
export const QUICK_REPLACE_REASONS = [
    'no_time',
    'not_in_mood',
    'missing_ingredient',
    'want_simple',
    'want_specific',
];
export const QuickReplacePayloadSchema = z.object({
    date: z.string().date(),
    meal_type: z.enum(MEAL_TYPES),
    member_ids: z.array(memberIdSchema).min(1),
    reason: z.enum(QUICK_REPLACE_REASONS),
    details: z.string().optional(),
    exclude_recipe_ids: z.array(z.string().uuid()).default([]),
});
//# sourceMappingURL=ai-recipes.js.map